import React from "react";
import { File, FileCheck2, FileInput, FileX2 } from "lucide-react";
import moment from "moment";
import "moment/locale/es";

moment.locale("es");

/**
 * Calcula la fecha de facturación basándose en el período, considerando años cruzados
 * Acepta objetos con { startDate, endDate, periodo } o { desde, hasta, periodo }.
 * `desde`/`hasta` se parsean como `DD/MM/YYYY`.
 * @param {{ startDate?: string, endDate?: string, desde?: string, hasta?: string, periodo?: string }} periodoActual
 * @returns {string|null} invoiceDate en formato YYYY-MM-DD
 */
export const calcularInvoiceDate = (periodoActual) => {
  if (!periodoActual || typeof periodoActual !== "object") return null;

  const startRaw = periodoActual.startDate || periodoActual.desde;
  const endRaw = periodoActual.endDate || periodoActual.hasta;

  if (!startRaw) return null;

  const parseDate = (raw) => {
    if (!raw) return null;
    if (typeof raw !== "string") return moment(raw);
    return raw.includes("/") ? moment(raw, "DD/MM/YYYY", true) : moment(raw);
  };

  const startMoment = parseDate(startRaw);
  const endMoment = endRaw ? parseDate(endRaw) : null;

  if (!startMoment || !startMoment.isValid()) return null;

  const hoy = moment();

  // primero intenta con periodo (nombre), si no existe, usa el mes de startMoment
  let mesNumeroPeriodo;
  if (periodoActual.periodo) {
    mesNumeroPeriodo = moment().month(periodoActual.periodo.toLowerCase()).month();
    if (Number.isNaN(mesNumeroPeriodo)) return null;
  } else {
    mesNumeroPeriodo = startMoment.month();
  }

  let anoPeriodo = startMoment.year();

  if (endMoment && endMoment.isValid()) {
    if (mesNumeroPeriodo === startMoment.month()) {
      anoPeriodo = startMoment.year();
    } else if (mesNumeroPeriodo === endMoment.month()) {
      anoPeriodo = endMoment.year();
    } else {
      anoPeriodo = startMoment.year();
    }
  }

  if (hoy.month() === mesNumeroPeriodo && hoy.year() === anoPeriodo) {
    return hoy.format("YYYY-MM-DD");
  }

  return moment().year(anoPeriodo).month(mesNumeroPeriodo).endOf("month").format("YYYY-MM-DD");
};

/**
 * Formatea un número con separador de miles (.) y decimales con coma (,)
 * @param {number} total - El número a formatear
 * @returns {string} Número formateado como string
 */
export const formatearTotal = (total) => {
  return total.toLocaleString("es-AR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

/**
 * Retorna una promesa que resuelve con opciones formateadas para un select async
 * @param {string} inputValue - Valor del input
 * @param {string} searchParam - Nombre del parámetro de búsqueda
 * @param {(params: Object) => Promise<any>} getFunction - Función que hace la request
 * @param {Function|undefined} objetoReturn - Función opcional para personalizar cada opción
 * @returns {Promise<Array<{ label: string, value: string|number }>>}
 */
export const promiseOptions = (inputValue, searchParam, getFunction, objetoReturn, addParams) =>
  new Promise((resolve) => {
    setTimeout(async () => {
      const params = {
        [searchParam]: inputValue,
        page: 1,
        perpage: 100,
        ...addParams,
      };

      try {
        const response = await getFunction(params);
        const subclients = response.result;
        const { rows } = subclients || {};

        const options =
          rows && rows.length > 0
            ? rows.map((e) => {
                if (typeof objetoReturn === "function") {
                  return objetoReturn(e);
                }
                return {
                  label: e.razonSocial,
                  value: e.itemId,
                };
              })
            : [];

        resolve(options);
      } catch (error) {
        resolve([]);
      }
    }, 100);
  });

/**
 * Devuelve un color hex según el estado
 * @param {number} estado
 * @returns {string}
 */
export const getColorByStatus = (estado) => {
  switch (estado) {
    case 1:
      return "#808080"; // Gris
    case 2:
    case 7:
      return "#e5c53c"; // Amarillo
    case 3:
      return "#008000"; // Verde
    case 4:
    case 5:
      return "#FF0000"; // Rojo
    default:
      return "#293134";
  }
};

/**
 * Devuelve un ícono de Lucide según el estado
 * @param {number} estado
 * @returns {JSX.Element}
 */
export const getIconByStatus = (estado) => {
  switch (estado) {
    case 1:
      return <FileInput size={14} />;
    case 2:
    case 7:
      return <FileCheck2 size={14} />;
    case 3:
      return <File size={14} />;
    case 4:
    case 5:
      return <FileX2 size={14} />;
    default:
      return <File size={14} />;
  }
};

/**
 * Devuelve la descripción del estado de una OV
 * @param {number} value
 * @returns {string}
 */
export const getEstadoByValueOV = (value) => {
  switch (value) {
    case 1:
      return "Enviar a Facturar";
    case 2:
    case 3:
    case 7:
      return "Enviado a Facturar";
    case 4:
    case 5:
    case 6:
      return "Error o Rechazado";
    default:
      return "Sin estado";
  }
};

/**
 * Valida si un email tiene un formato válido
 * @param {string} email
 * @returns {boolean}
 */
export const isValidEmail = (email) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

/**
 * Devuelve el periodo en formato MM/YYYY
 * @param {string|Date} fecha
 * @returns {string}
 */
export const formatPeriodo = (fecha) => {
  const date = new Date(fecha);
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${month}/${year}`;
};

/**
 * Busca un PI por su ID y devuelve su nombre
 * @param {string|number} id
 * @param {Array<{ itemId: string|number, nombreApellido: string }>} pis
 * @returns {string}
 */
export const getPiById = (id, pis) => {
  const pi = pis.find((objeto) => objeto.itemId === id);
  return pi ? pi.nombreApellido : "-";
};

export const parseDDMMYYYYtoDate = (str) => {
  if (!str) return null;
  const [day, month, year] = str.split("/").map(Number);
  return new Date(year, month - 1, day);
};

export const formatDateToDDMMYYYY = (date) => {
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

export function removeLeadingZeros(str) {
  if (str === null || str === undefined) return "";
  return str.replace(/^0+(?=[A-Za-z1-9])/, "");
}
